import { useEffect, useRef, useState } from "react";

import { HolderConfigurationSchema } from "@thalamos/haven-common";
import type { ConfigurationScopeId, SettingView } from "@thalamos/haven-common";

import api from "../../api/index.js";
import type { ReadOutcome } from "./readOutcome.js";
import type { HolderEndpoints } from "./useHolderConfiguration.js";

/** Reads one configuration's resolved settings. */
export type ReadScopeSettings = (
  scopeId: ConfigurationScopeId,
) => ReturnType<HolderEndpoints["read"]>;

export const readScopeSettingsFromApi: ReadScopeSettings = (scopeId) =>
  api.configuration.scopeSettings(scopeId);

/**
 * The resolved settings of each configuration named, each read once for as
 * long as the caller is mounted. One not yet asked for reads as still reading.
 */
export const useSettingsOfScopes = ({
  scopeIds,
  read,
}: {
  scopeIds: readonly ConfigurationScopeId[];
  read: ReadScopeSettings;
}): ((
  scopeId: ConfigurationScopeId,
) => ReadOutcome<readonly SettingView[]>) => {
  const [settings, setSettings] = useState<
    ReadonlyMap<ConfigurationScopeId, ReadOutcome<readonly SettingView[]>>
  >(new Map<ConfigurationScopeId, ReadOutcome<readonly SettingView[]>>());
  // Every render passes a new array, so what has been asked for is remembered
  // rather than inferred from the array changing.
  const requested = useRef(new Set<ConfigurationScopeId>());
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    return () => {
      mounted.current = false;
    };
  }, []);

  useEffect(() => {
    const settle = (
      scopeId: ConfigurationScopeId,
      outcome: ReadOutcome<readonly SettingView[]>,
    ) => {
      if (mounted.current) {
        setSettings((known) => new Map(known).set(scopeId, outcome));
      }
    };

    for (const scopeId of scopeIds) {
      if (requested.current.has(scopeId)) {
        continue;
      }

      requested.current.add(scopeId);

      // Started inside the promise, so a read that throws is a failed read
      // rather than an error escaping the effect.
      Promise.resolve()
        .then(() => read(scopeId))
        .then((response) => {
          const parsed =
            response.status === 200
              ? HolderConfigurationSchema.safeParse(response.data)
              : undefined;

          settle(
            scopeId,
            parsed?.success === true
              ? { kind: "read", value: parsed.data.settings }
              : { kind: "failed" },
          );
        })
        .catch(() => {
          settle(scopeId, { kind: "failed" });
        });
    }
  }, [scopeIds, read]);

  return (scopeId) => settings.get(scopeId) ?? { kind: "reading" };
};
